/**
 * 日時は UTC で保存し、JST で表示・判定する（02 2.11・9.9、要件書 5.4・9.2）。
 * Date を直接比べない。月・締め日・期限の判定はこのファイルの関数だけを使う（02 11.2 No.6）。
 *
 * - 日時：ISO 8601 の UTC 文字列（`2026-09-25T02:00:00.000Z`）
 * - 日付のみ：JST の `YYYY-MM-DD`
 * - 年月：JST の `YYYY-MM`
 * - TTL：UNIX 秒
 *
 * JST は夏時間が無く常に UTC+9 のため、オフセットの固定値で変換する。
 */

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const YEAR_MONTH_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;
const UTC_DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;

let fixedClock: (() => number) | null = null;

/** テストだけで使う：現在時刻を固定する（null で戻す） */
export function setClockForTest(utc: string | null): void {
  if (utc === null) {
    fixedClock = null;
    return;
  }
  const fixed_ms = parseUtc(utc);
  fixedClock = () => fixed_ms;
}

function currentMs(): number {
  return fixedClock ? fixedClock() : Date.now();
}

function pad(value: number, width: number): string {
  return String(value).padStart(width, '0');
}

function parseUtc(utc: string): number {
  if (!UTC_DATETIME_PATTERN.test(utc)) {
    throw new RangeError(`UTC の ISO 8601 文字列ではありません: ${utc}`);
  }
  return Date.parse(utc);
}

function parseDateParts(date: string): { year: number; month: number; day: number } {
  const matched = DATE_PATTERN.exec(date);
  if (!matched) {
    throw new RangeError(`YYYY-MM-DD ではありません: ${date}`);
  }
  return { year: Number(matched[1]), month: Number(matched[2]), day: Number(matched[3]) };
}

/** JST の日付の 0:00 を UTC のミリ秒にする */
function jstMidnightMs(date: string): number {
  const { year, month, day } = parseDateParts(date);
  return Date.UTC(year, month - 1, day) - JST_OFFSET_MS;
}

function formatJstDateFromMs(ms: number): string {
  const shifted = new Date(ms + JST_OFFSET_MS);
  return `${pad(shifted.getUTCFullYear(), 4)}-${pad(shifted.getUTCMonth() + 1, 2)}-${pad(shifted.getUTCDate(), 2)}`;
}

/** 現在の UTC（ISO 8601） */
export function nowUtc(): string {
  return new Date(currentMs()).toISOString();
}

/** UTC の日時を秒・ミリ秒だけずらす（有効期限の計算用） */
export function addSecondsUtc(utc: string, seconds: number): string {
  return new Date(parseUtc(utc) + seconds * 1000).toISOString();
}

/** UTC の日時 → JST の日付（YYYY-MM-DD） */
export function toJstDate(utc: string): string {
  return formatJstDateFromMs(parseUtc(utc));
}

/** UTC の日時 → JST の年月（YYYY-MM） */
export function jstYearMonth(utc: string): string {
  return toJstDate(utc).slice(0, 7);
}

/** JST の今日（YYYY-MM-DD） */
export function jstToday(): string {
  return formatJstDateFromMs(currentMs());
}

/** JST の当月（YYYY-MM） */
export function jstCurrentYearMonth(): string {
  return jstToday().slice(0, 7);
}

/** JST の日付の開始（0:00:00.000）を UTC の ISO 文字列にする */
export function jstStartOfDayUtc(date: string): string {
  return new Date(jstMidnightMs(date)).toISOString();
}

/** JST の日付の終わり（23:59:59.999）を UTC の ISO 文字列にする */
export function jstEndOfDayUtc(date: string): string {
  return new Date(jstMidnightMs(date) + DAY_MS - 1).toISOString();
}

/** 実在する日付か（zDate の refine） */
export function isValidJstDate(date: string): boolean {
  const matched = DATE_PATTERN.exec(date);
  if (!matched) {
    return false;
  }
  const { year, month, day } = parseDateParts(date);
  const probe = new Date(Date.UTC(year, month - 1, day));
  return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
}

/** 年月の範囲（JST の月初・月末と、その UTC の範囲） */
export function jstMonthRange(year_month: string): {
  start_date: string;
  end_date: string;
  start_utc: string;
  end_utc: string;
} {
  const matched = YEAR_MONTH_PATTERN.exec(year_month);
  if (!matched) {
    throw new RangeError(`YYYY-MM ではありません: ${year_month}`);
  }
  const year = Number(matched[1]);
  const month = Number(matched[2]);
  const last_day = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const start_date = `${pad(year, 4)}-${pad(month, 2)}-01`;
  const end_date = `${pad(year, 4)}-${pad(month, 2)}-${pad(last_day, 2)}`;
  return { start_date, end_date, start_utc: jstStartOfDayUtc(start_date), end_utc: jstEndOfDayUtc(end_date) };
}

/** 日付（YYYY-MM-DD）に n 日足す */
export function addDays(date: string, days: number): string {
  const { year, month, day } = parseDateParts(date);
  const shifted = new Date(Date.UTC(year, month - 1, day) + days * DAY_MS);
  return `${pad(shifted.getUTCFullYear(), 4)}-${pad(shifted.getUTCMonth() + 1, 2)}-${pad(shifted.getUTCDate(), 2)}`;
}

function dayOfWeek(date: string): number {
  const { year, month, day } = parseDateParts(date);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

function isWeekday(date: string): boolean {
  const weekday = dayOfWeek(date);
  return weekday !== 0 && weekday !== 6;
}

/** 日付に営業日（月〜金）を n 日足す。祝日は入力文書に定めが無いため考えない */
export function addBusinessDays(date: string, days: number): string {
  let current = date;
  let remaining = days;
  const step = days >= 0 ? 1 : -1;
  while (remaining !== 0) {
    current = addDays(current, step);
    if (isWeekday(current)) {
      remaining -= step;
    }
  }
  return current;
}

/** その月の n 番目の平日（月〜金）。期限ルール businessDayOfMonth で使う */
export function nthWeekdayOfMonth(year_month: string, nth: number): string {
  if (!Number.isInteger(nth) || nth < 1) {
    throw new RangeError(`nth は 1 以上の整数: ${nth}`);
  }
  const { start_date, end_date } = jstMonthRange(year_month);
  let current = start_date;
  let count = 0;
  for (;;) {
    if (isWeekday(current)) {
      count += 1;
      if (count === nth) {
        return current;
      }
    }
    if (current === end_date) {
      return end_date;
    }
    current = addDays(current, 1);
  }
}

/** 日付の差（b − a の日数） */
export function diffDays(date_a: string, date_b: string): number {
  return Math.round((jstMidnightMs(date_b) - jstMidnightMs(date_a)) / DAY_MS);
}

/** UTC の日時 → UNIX 秒（TTL 用） */
export function epochSeconds(utc: string): number {
  return Math.floor(parseUtc(utc) / 1000);
}

/** 現在の UNIX 秒 */
export function nowEpochSeconds(): number {
  return Math.floor(currentMs() / 1000);
}

/** UTC の日時どうしの比較（a が先なら負、同じなら 0、後なら正） */
export function compareUtc(utc_a: string, utc_b: string): number {
  return parseUtc(utc_a) - parseUtc(utc_b);
}

/** UTC の日時どうしの差（b − a の秒） */
export function diffSecondsUtc(utc_a: string, utc_b: string): number {
  return (parseUtc(utc_b) - parseUtc(utc_a)) / 1000;
}

/** TTL の期限（UNIX 秒）を過ぎたか。TTL の削除は遅れるため、期限はこの関数で必ず比べる（02 11.2 No.17） */
export function isEpochExpired(expires_at: number): boolean {
  return nowEpochSeconds() >= expires_at;
}

/** 日付（YYYY-MM-DD）どうしの比較 */
export function compareDate(date_a: string, date_b: string): number {
  return diffDays(date_b, date_a);
}

/** 年月（YYYY-MM）に n か月足す */
export function addMonths(year_month: string, months: number): string {
  const matched = YEAR_MONTH_PATTERN.exec(year_month);
  if (!matched) {
    throw new RangeError(`YYYY-MM ではありません: ${year_month}`);
  }
  const index = Number(matched[1]) * 12 + (Number(matched[2]) - 1) + months;
  return `${pad(Math.floor(index / 12), 4)}-${pad((index % 12) + 1, 2)}`;
}

/** from〜to（YYYY-MM・両端を含む）の年月の一覧 */
export function yearMonthsBetween(from_year_month: string, to_year_month: string): string[] {
  const months: string[] = [];
  for (let current = from_year_month; current <= to_year_month; current = addMonths(current, 1)) {
    months.push(current);
  }
  return months;
}

/** 日付（YYYY-MM-DD）を含む四半期（1〜3・4〜6・7〜9・10〜12 月）の初日・末日 */
export function jstQuarterRange(date: string): { start_date: string; end_date: string } {
  const { year, month } = parseDateParts(date);
  const first_month = Math.floor((month - 1) / 3) * 3 + 1;
  const start = `${pad(year, 4)}-${pad(first_month, 2)}`;
  return { start_date: `${start}-01`, end_date: jstMonthRange(addMonths(start, 2)).end_date };
}

/** 日付（YYYY-MM-DD）を含む年（1/1〜12/31） */
export function jstYearRange(date: string): { start_date: string; end_date: string } {
  const { year } = parseDateParts(date);
  return { start_date: `${pad(year, 4)}-01-01`, end_date: `${pad(year, 4)}-12-31` };
}

/** 相対の期間の種類（02 D-050 の relative_type。画面のよく使う期間も同じ：03 FE-D-085） */
export const RELATIVE_PERIOD_TYPES = ['thisMonth', 'lastMonth', 'thisQuarter', 'thisYear', 'last30Days'] as const;
export type RelativePeriodType = (typeof RELATIVE_PERIOD_TYPES)[number];

/** 相対の期間を JST の日付の範囲にする（today は JST の今日。既定は jstToday()） */
export function relativePeriodRange(
  relative_type: RelativePeriodType,
  today: string = jstToday(),
): { from: string; to: string } {
  const year_month = today.slice(0, 7);
  switch (relative_type) {
    case 'thisMonth': {
      const range = jstMonthRange(year_month);
      return { from: range.start_date, to: range.end_date };
    }
    case 'lastMonth': {
      const range = jstMonthRange(addMonths(year_month, -1));
      return { from: range.start_date, to: range.end_date };
    }
    case 'thisQuarter': {
      const range = jstQuarterRange(today);
      return { from: range.start_date, to: range.end_date };
    }
    case 'thisYear': {
      const range = jstYearRange(today);
      return { from: range.start_date, to: range.end_date };
    }
    case 'last30Days':
      return { from: addDays(today, -29), to: today };
  }
}

/**
 * 今週（月曜〜今日。JST）の範囲（274：旧PCRM のレポートの期間「今週」）。
 * 雛形の relative_type（RELATIVE_PERIOD_TYPES・API の検証）には入れない（画面の期間の選択だけで使う）
 */
export function jstThisWeekRange(today: string = jstToday()): { from: string; to: string } {
  const weekday = dayOfWeek(today);
  return { from: addDays(today, weekday === 0 ? -6 : 1 - weekday), to: today };
}

/** 日付（YYYY-MM-DD）に n か月足す（月末を超える日はその月の末日に寄せる。274：新規目標の終了日の初期値） */
export function addMonthsToDate(date: string, months: number): string {
  const { day } = parseDateParts(date);
  const year_month = addMonths(date.slice(0, 7), months);
  const last_day = Number(jstMonthRange(year_month).end_date.slice(8, 10));
  return `${year_month}-${pad(Math.min(day, last_day), 2)}`;
}

/** 期限ルールの書式（02 D-055）：dayOfMonth:<1-31>・endOfMonth・businessDayOfMonth:<1-23>・nextMonthDay:<1-31> */
export const DUE_DATE_RULE_PATTERN =
  /^(?:endOfMonth|dayOfMonth:(?:[1-9]|[12]\d|3[01])|businessDayOfMonth:(?:[1-9]|1\d|2[0-3])|nextMonthDay:(?:[1-9]|[12]\d|3[01]))$/;

function dayOfMonthOrLast(year_month: string, day: number): string {
  const { end_date } = jstMonthRange(year_month);
  const last_day = Number(end_date.slice(8, 10));
  return `${year_month}-${pad(Math.min(day, last_day), 2)}`;
}

/**
 * 月次タスクの期限（JST の日付）：対象月と雛形項目の期限ルールから決める（02 D-055）。
 * null・空は対象月の末日。月末を超える日は末日にする。平日（月〜金）の数は祝日を考えない。
 */
export function dueDateOfRule(rule: string | null | undefined, year_month: string): string {
  if (!rule || rule === 'endOfMonth') {
    return jstMonthRange(year_month).end_date;
  }
  if (!DUE_DATE_RULE_PATTERN.test(rule)) {
    throw new RangeError(`期限ルールの書式が正しくありません: ${rule}`);
  }
  const [kind = '', value = ''] = rule.split(':');
  const day = Number(value);
  switch (kind) {
    case 'dayOfMonth':
      return dayOfMonthOrLast(year_month, day);
    case 'businessDayOfMonth':
      return nthWeekdayOfMonth(year_month, day);
    default:
      return dayOfMonthOrLast(addMonths(year_month, 1), day);
  }
}

/** UTC の日時 → JST の `YYYY/MM/DD HH:mm:ss`（CSV・メール本文の日時：02 5.6.1 D-068） */
export function toJstDateTimeText(utc: string): string {
  const shifted = new Date(parseUtc(utc) + JST_OFFSET_MS);
  return (
    `${pad(shifted.getUTCFullYear(), 4)}/${pad(shifted.getUTCMonth() + 1, 2)}/${pad(shifted.getUTCDate(), 2)} ` +
    `${pad(shifted.getUTCHours(), 2)}:${pad(shifted.getUTCMinutes(), 2)}:${pad(shifted.getUTCSeconds(), 2)}`
  );
}

/** JST の `YYYY/MM/DD HH:mm:ss`（`YYYY-MM-DD HH:mm(:ss)` も可）→ UTC の ISO 文字列。形が違えば null（CSV 取込の日時） */
export function parseJstDateTimeText(text: string): string | null {
  const matched = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(text.trim());
  if (!matched) {
    return null;
  }
  const [year, month, day, hour, minute, second] = matched.slice(1).map((part) => Number(part ?? '0')) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  const date = `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
  if (!isValidJstDate(date) || hour > 23 || minute > 59 || second > 59) {
    return null;
  }
  return new Date(Date.UTC(year, month - 1, day, hour, minute, second) - JST_OFFSET_MS).toISOString();
}

/** サーバーの JST 時刻の `YYYYMMDD_HHMMSS`（出力のファイル名：02 5.6.1） */
export function jstFileTimestamp(utc: string): string {
  return toJstDateTimeText(utc).replace(/\//g, '').replace(':', '').replace(':', '').replace(' ', '_');
}

/** JST の今の時刻の枠 `YYYY-MM-DDTHH`（1 日に何度も動く定時バッチの冪等の枠：見張り・容量監視） */
export function jstCurrentHourSlot(): string {
  const shifted = new Date(currentMs() + JST_OFFSET_MS);
  return `${formatJstDateFromMs(currentMs())}T${pad(shifted.getUTCHours(), 2)}`;
}

/** JST の今の時（0〜23） */
export function jstCurrentHour(): number {
  return new Date(currentMs() + JST_OFFSET_MS).getUTCHours();
}

/** 日付（YYYY-MM-DD）に n 年足す（2/29 は 2/28 にする。保管期限の計算：02 8.2 INT-14） */
export function addYears(date: string, years: number): string {
  const { year, month, day } = parseDateParts(date);
  const target_year = year + years;
  const last_day = new Date(Date.UTC(target_year, month, 0)).getUTCDate();
  return `${pad(target_year, 4)}-${pad(month, 2)}-${pad(Math.min(day, last_day), 2)}`;
}
